import { useState, useRef, useEffect, useMemo } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const genSessionId = () =>
    "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

const INITIAL_GREETING =
    "Hi — I'm The HR Connect Assistant. Ask me anything about the Ignite Health plan, what's included, how it works with existing coverage, or the sign-up process.";

const SUGGESTED_PROMPTS = [
    "What's included?",
    "Does it replace my plan?",
    "How much does it cost?",
];

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: INITIAL_GREETING },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);

    const sessionId = useMemo(() => genSessionId(), []);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async (e, overrideText) => {
        e?.preventDefault();
        const text = (overrideText ?? input).trim();
        if (!text || busy) return;
        if (!overrideText) setInput("");
        setMessages((m) => [...m, { role: "user", content: text }]);
        setBusy(true);
        try {
            const res = await fetch(`${API}/chat/message`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, text }),
            });
            if (!res.ok) throw new Error("chat failed");
            const data = await res.json();
            setMessages((m) => [...m, { role: "assistant", content: data.response }]);
        } catch {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "Sorry — the chat is briefly unavailable. Please try again in a moment.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const showPrompts = messages.length === 1 && !busy;

    return (
        <>
            {/* Launcher */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="chat-launcher"
                    className="fixed bottom-20 right-6 z-40 inline-flex items-center gap-2 bg-brand text-brand-foreground hover:bg-brand-hover px-5 py-3.5 rounded-full text-sm font-semibold shadow-lg shadow-black/20 transition-all hover:shadow-xl"
                    aria-label="Open chat"
                >
                    <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
                    Chat with The HR Connect Assistant
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed bottom-20 right-6 z-40 w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-6rem))] bg-background border border-border shadow-2xl shadow-black/25 rounded-sm flex flex-col overflow-hidden"
                    role="dialog"
                    aria-label="Chat with The HR Connect"
                >
                    <header className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface/60">
                        <div>
                            <p className="eyebrow mb-0.5">Chat</p>
                            <p className="font-heading text-sm font-semibold text-navy">
                                The HR Connect Assistant
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="chat-close"
                            className="text-ink-soft hover:text-foreground"
                            aria-label="Close chat"
                        >
                            <X className="h-5 w-5" strokeWidth={1.5} />
                        </button>
                    </header>

                    <div
                        ref={scrollRef}
                        data-testid="chat-messages"
                        className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-background"
                    >
                        {messages.map((m, i) => (
                            <MessageBubble key={i} role={m.role} content={m.content} />
                        ))}
                        {showPrompts && (
                            <div
                                data-testid="chat-suggested-prompts"
                                className="flex flex-wrap gap-2 pt-1"
                            >
                                {SUGGESTED_PROMPTS.map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => send(null, p)}
                                        data-testid={`chat-prompt-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="text-xs px-3 py-1.5 border border-border bg-surface/60 hover:bg-brand hover:text-brand-foreground hover:border-brand rounded-full text-ink-soft transition-colors"
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        )}
                        {busy && (
                            <div className="flex items-center gap-2 text-ink-soft text-xs" data-testid="chat-typing">
                                <span className="h-1.5 w-1.5 rounded-full bg-navy animate-pulse" />
                                <span className="h-1.5 w-1.5 rounded-full bg-navy animate-pulse" style={{ animationDelay: "150ms" }} />
                                <span className="h-1.5 w-1.5 rounded-full bg-navy animate-pulse" style={{ animationDelay: "300ms" }} />
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={send}
                        className="flex items-center gap-2 border-t border-border px-3 py-3 bg-background"
                        data-testid="chat-input-form"
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your question…"
                            data-testid="chat-input"
                            disabled={busy}
                            className="flex-1 bg-transparent px-2 py-2 text-sm text-foreground placeholder:text-ink-soft/60 focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            data-testid="chat-send"
                            aria-label="Send"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-sm bg-brand text-brand-foreground hover:bg-brand-hover disabled:opacity-40 transition-colors"
                        >
                            <Send className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

function MessageBubble({ role, content }) {
    const isUser = role === "user";
    return (
        <div
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}
            data-testid={`chat-msg-${role}`}
        >
            <div
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    isUser
                        ? "bg-navy text-background rounded-sm rounded-br-none"
                        : "bg-surface text-foreground rounded-sm rounded-bl-none border border-border"
                }`}
            >
                {content}
            </div>
        </div>
    );
}
